import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import type { FunctionStats } from '@/lib/sdk/profiling';

interface TopFunctionsTableProps {
  functions: FunctionStats[];
  maxItems?: number;
}

export function TopFunctionsTable({ functions, maxItems = 20 }: TopFunctionsTableProps) {
  const displayFunctions = functions.slice(0, maxItems);
  const rowGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  if (!functions.length) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="text-muted">No function data available</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto" borderWidth="1px" borderColor="border-default" borderRadius="lg">
      <Table variant="simple" size="sm">
        <Thead bg="bg-muted">
          <Tr>
            <Th color="text-primary" borderColor="border-default">Function</Th>
            <Th color="text-primary" borderColor="border-default">Package</Th>
            <Th color="text-primary" borderColor="border-default" isNumeric>Flat</Th>
            <Th color="text-primary" borderColor="border-default" isNumeric>Flat %</Th>
            <Th color="text-primary" borderColor="border-default" isNumeric>Cum</Th>
            <Th color="text-primary" borderColor="border-default" isNumeric>Cum %</Th>
            <Th color="text-primary" borderColor="border-default" w="150px">Distribution</Th>
          </Tr>
        </Thead>
        <Tbody>
          {displayFunctions.map((fn, index) => (
            <Tr key={index} bgGradient={rowGradient} _hover={{ bg: 'interactive-hover' }}>
              <Td borderColor="border-default">
                <Tooltip label={fn.function_name} placement="top-start">
                  <Text
                    color="text-primary"
                    fontSize="sm"
                    fontFamily="mono"
                    maxW="300px"
                    isTruncated
                  >
                    {fn.function_name.split('/').pop() || fn.function_name}
                  </Text>
                </Tooltip>
              </Td>
              <Td borderColor="border-default">
                <Text
                  color="text-secondary"
                  fontSize="sm"
                  fontFamily="mono"
                  maxW="200px"
                  isTruncated
                >
                  {fn.package_name}
                </Text>
              </Td>
              <Td borderColor="border-default" isNumeric>
                <Text color="text-primary" fontSize="sm" fontFamily="mono">
                  {fn.flat_value.toLocaleString()}
                </Text>
              </Td>
              <Td borderColor="border-default" isNumeric>
                <Text color="text-primary" fontSize="sm">
                  {fn.flat_percent.toFixed(2)}%
                </Text>
              </Td>
              <Td borderColor="border-default" isNumeric>
                <Text color="text-primary" fontSize="sm" fontFamily="mono">
                  {fn.cum_value.toLocaleString()}
                </Text>
              </Td>
              <Td borderColor="border-default" isNumeric>
                <Text color="text-primary" fontSize="sm">
                  {fn.cum_percent.toFixed(2)}%
                </Text>
              </Td>
              <Td borderColor="border-default">
                <Progress
                  value={fn.flat_percent}
                  size="sm"
                  colorScheme={fn.flat_percent > 20 ? 'red' : fn.flat_percent > 10 ? 'orange' : 'green'}
                  borderRadius="full"
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
